import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

/**
 * One-shot haptic feedback, best-effort and never required.
 *
 * Two layers, tried in order, each fully guarded:
 *   1. The Capacitor Haptics plugin (`@capacitor/haptics`), on a native
 *      shell only — `Capacitor.isNativePlatform()`. This is the real
 *      iOS Taptic / Android vibrator path.
 *   2. `navigator.vibrate` — the web / desktop-browser fallback, and the
 *      path taken on any native shell whose plugin call refuses.
 *   3. Nothing. A missing haptic is never an error: it is a garnish on an
 *      interaction that has already reported itself visually.
 *
 * Deliberately synchronous-looking and `void`: the plugin call returns a
 * promise which is swallowed (rejection included), because nothing
 * downstream may wait on or branch on whether the device buzzed.
 */

/**
 * A short, deliberate "that landed" tap. Used by the reaction bar's
 * hold-to-super gesture at the moment the hold completes, which is the one
 * point in the gesture with no other confirmation yet.
 */
export function hapticImpact(durationMs = 18): void {
  try {
    if (Capacitor.isNativePlatform()) {
      // A rejection here is swallowed, not rethrown and not fallen through
      // from: the native shell owns the haptic, and a second buzz from the
      // web path would be a double tap on the devices that did fire.
      void Promise.resolve(Haptics.impact({ style: ImpactStyle.Medium })).catch(() => {
        /* device has no vibrator, or the OS refused — ignore */
      });
      return;
    }
  } catch {
    /* plugin unavailable or threw synchronously — fall through to the web path */
  }
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(durationMs);
    }
  } catch {
    /* vibration blocked (permissions policy, user gesture rules) — ignore */
  }
}

/**
 * R14: "a call is ringing for you": the incoming-call sheet appearing. A
 * longer buzz than `hapticImpact`, and never a sound. Same two layers, same
 * guards: the native vibrator on a Capacitor shell, else
 * `navigator.vibrate` (a no-op on desktop browsers, which is the intent).
 */
export function hapticRing(): void {
  try {
    if (Capacitor.isNativePlatform()) {
      void Promise.resolve(Haptics.vibrate({ duration: 600 })).catch(() => {
        /* device has no vibrator, or the OS refused — ignore */
      });
      return;
    }
  } catch {
    /* plugin unavailable or threw synchronously — fall through to the web path */
  }
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate([400, 200, 400]);
    }
  } catch {
    /* vibration blocked (permissions policy, user gesture rules) — ignore */
  }
}
